<template>
    <Layout>
        <template v-slot:title>
            <h1>User Quiz</h1>
            <div class="pt-2 text-red text-bold">Records found: {{ totalItems }}</div>
        </template>

        <template v-slot:content>
            <Loader v-if="isLoading"></Loader>
            <div class="row mb-4">
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-sm-0">
                    <select class="form-control" v-model="filterType">
                        <option value="">Filter by type</option>
                        <option value="poll">Poll</option>
                        <option value="quiz">Quiz</option>
                    </select>
                </div>
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-md-0">
                    <TagSelect @input="onFilterTagHandler" :tag-list="tagList" v-model="filterTag"></TagSelect>
                </div>
                <div class="col-sm-6 col-md-4 col-xl-2 mb-2 mb-md-2">
                    <form @submit.prevent="onSearch" class="input-group btn-inline">
                        <input
                            v-model="keyword"
                            name="table_search"
                            class="form-control float-right"
                            placeholder="Search"
                            type="text"
                        />

                        <div class="input-group-append">
                            <button type="submit" class="btn btn-default">
                                <f-icon icon="search" />
                            </button>
                        </div>
                    </form>
                </div>
                <div class="col-sm-6 col-md-4 col-xl-2">
                    <div
                        v-tippy="{ placement: 'top' }"
                        @click="resetFilter"
                        content="Reset filter"
                        style="min-width: 0px; padding: 0.28rem 1rem"
                        class="btn btn-sm btn-secondary text-lg"
                    >
                        <i class="fas fa-eraser"></i>
                    </div>
                </div>
            </div>

            <div v-if="list.length > 0">
                <div class="row">
                    <div class="col-12">
                        <div class="table-responsive tbq-table-responsive">
                            <table class="table tbq-table">
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>
                                            <SortLink sort-key="title">Title</SortLink>
                                        </th>
                                        <th>Tag</th>
                                        <th>User</th>
                                        <th class="text-center">
                                            <SortLink sort-key="type">Type</SortLink>
                                        </th>
                                        <th class="text-center">
                                            <SortLink sort-key="question_count">Questions</SortLink>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in list" :key="index">
                                        <td class="thin-cell text-center align-middle" nowrap>
                                            <router-link
                                                tag="a"
                                                :title="item.title"
                                                :to="{
                                                    name: 'quizzes.user.questions',
                                                    params: {
                                                        id: item._id.$oid
                                                    }
                                                }"
                                            >
                                                <div
                                                    class="bg-image"
                                                    :style="`background-image: url(${getQuizImage(item, true)})`"
                                                ></div>
                                            </router-link>
                                        </td>
                                        <td class="align-middle" style="min-width: 200px; max-width: 250px">
                                            <router-link
                                                tag="a"
                                                :title="item.title"
                                                :to="{
                                                    name: 'quizzes.pending-approval.questions',
                                                    params: {
                                                        id: item._id.$oid
                                                    }
                                                }"
                                                class="text-bold"
                                            >
                                                {{ item.title }}
                                            </router-link>
                                            <small class="d-block mb-0">Limit player: {{ item.limit_player }}</small>
                                            <small class="d-block mb-0">
                                                Played: {{ toNumber(item.playing_counter) }}
                                            </small>
                                            <small v-if="item.show_in_front_page" class="text-green mb-0">
                                                Show in front page
                                            </small>
                                        </td>
                                        <td class="align-middle">
                                            <Tags :tags="item.tags_name"></Tags>
                                        </td>
                                        <td class="align-middle">
                                            <div v-if="item.user && item.user.name && item.user.email">
                                                <p class="mb-1 d-flex">
                                                    <UserIcon class="mr-2" size="1.2x" />
                                                    <a href="#" @click.prevent="filterByUser(item.user._id.$oid)">
                                                        {{ item.user.name }}
                                                    </a>
                                                </p>
                                                <p class="mb-1 d-flex">
                                                    <MailIcon class="mr-2" size="1.2x" /> {{ item.user.email }}
                                                </p>
                                            </div>
                                            <div v-else-if="!item.user.name && !item.user.email" class="text-gray">
                                                [Deleted User]
                                            </div>
                                            <div v-else>
                                                {{ item.email }}
                                            </div>
                                        </td>
                                        <td class="align-middle text-center text-capitalize">
                                            <span
                                                :class="{
                                                    'bg-info': item.type === 'poll',
                                                    'bg-success': item.type === 'quiz',
                                                    'px-3 py-1 rounded-pill': true
                                                }"
                                            >
                                                {{ item.type }}
                                            </span>
                                        </td>
                                        <td class="thin-cell text-center align-middle" nowrap>
                                            {{ item.sumQuestion }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="col-12 my-3">
                        <Paginator
                            @change="onPageChange"
                            :current-page="currentPage"
                            :page-count="pageCount"
                            :total-items="totalItems"
                            ref="setPage"
                        />
                    </div>
                </div>
            </div>
            <EmptyList v-else :label="emptyLabel" />
        </template>
    </Layout>
</template>

<script>
import helperMixin from '@/mixins/helper'
import Layout from '@/components/Layout'
import Loader from '@/components/Loader'
import EmptyList from '@/components/EmptyList'
import Url from '@/service/url'
import Paginator from '@/components/Paginator'
import { UserIcon, MailIcon } from 'vue-feather-icons'
import SortLink from '@/components/SortLink'
import Tags from '@/components/Tags'
import TagSelect from '@/components/TagSelect'
import { mapGetters } from 'vuex'

export default {
    name: 'quiz-approval-pending-index',

    mixins: [helperMixin],

    components: {
        Layout,
        Loader,
        EmptyList,
        Paginator,
        UserIcon,
        MailIcon,
        SortLink,
        Tags,
        TagSelect
    },

    created() {
        this.fetchTag().then(() => this.findMany())
    },

    data() {
        return {
            list: [],
            isLoading: false,
            keyword: '',
            filterType: '',
            filterTag: [],

            currentPage: 1,
            pageCount: 0,
            totalItems: 0,

            emptyLabel: 'No data in list.',
            tagList: []
        }
    },

    computed: {
        ...mapGetters('tag', {
            tags: 'tags'
        })
    },

    watch: {
        $route() {
            const url = new Url()

            this.selectedStatus = ''

            this.findMany(url.buildQuery())
            const query = url.getQuery()

            if (!query.tag) {
                this.filterTag = []
            }
        },

        filterType() {
            this.onSearch()
        },

        tags(val) {
            let tags = val
            let newTags = []

            for (let x in tags) {
                newTags.push({
                    label: tags[x].name,
                    code: tags[x]._id.$oid,
                    type: tags[x].type
                })
            }

            this.tagList = newTags
        }
    },

    methods: {
        findMany() {
            const url = new Url()
            const query = url.buildQuery()
            const self = this

            self.isLoading = true

            self.$store
                .dispatch('quiz/approvalQuizFindMany', { query: query, approval_status: ['pending'] })
                .then(res => {
                    const quiz = res.data.data
                    self.isLoading = false
                    self.list = quiz.data

                    self.pageCount = quiz.page_count
                    self.totalItems = quiz.total
                    self.currentPage = parseFloat(quiz.current_page)
                    self.keyword = query.keyword
                    self.filterType = query.type ? query.type : ''

                    self.list.map(item => {
                        item.sumQuestion = item.question_count
                    })

                    if (query.tag) {
                        self.filterTag = Array.isArray(query.tag) ? query.tag : [query.tag]
                    }

                    if (self.keyword !== '' && self.list.length === 0) {
                        this.emptyLabel = `
                            <p class="text-center text-muted">Your search <b class="text-dark">"${this.keyword}"</b> did not match.</p>
                            <p class="text-center text-muted">Please, try another keyword</p>
                        `
                    }
                })
                .catch(() => {
                    self.isLoading = false
                    // window.shout.error('Quiz not found')
                })
        },

        onSearch() {
            const url = new Url()
            let query = url.buildQuery({ page: 1, keyword: this.keyword, type: this.filterType, tag: this.filterTag })

            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query
            })
        },

        onPageChange(page) {
            var url = new Url()

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query: url.buildQuery({ page })
            })
        },

        fetchTag() {
            this.isLoading = true
            return this.$store.dispatch('tag/findAll', { type: 'quiz' })
        },

        filterByUser(id) {
            var url = new Url()
            var query = url.getQuery()

            if (query.user_id == id) {
                return
            }

            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }

            this.$router.replace({
                name: this.$router.currentRoute.name,
                query: url.buildQuery({ user_id: id })
            })
        },

        onFilterTagHandler() {
            this.onSearch()
        },

        resetFilter() {
            this.filterTag = []
            this.filterType = ''
            this.keyword = ''
            if (this.list.length != 0) {
                this.$refs.setPage.setPage(0)
            }
            this.$router.replace({ name: this.$router.currentRoute.name })
        }
    }
}
</script>

<style lang="scss" scoped>
.col-xl-2 {
    @media (min-width: 1600px) {
        -webkit-box-flex: 0;
        -ms-flex: 0 0 20%;
        flex: 0 0 20%;
        max-width: 20%;
    }
    @media (min-width: 1200px) and (max-width: 1600px) {
        -webkit-box-flex: 0;
        -ms-flex: 0 0 25%;
        flex: 0 0 25%;
        max-width: 25%;
    }
}

.bg-image {
    width: 80px;
    height: 80px;
    border: 3px solid white;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 0.25rem;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.3);
}

.theme-dark-mode {
    .bg-image {
        border-color: var(--dark-theme-primary);
    }
}
</style>
